import { useEffect, useState } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometChatUIKitLoginListener } from "@cometchat/chat-uikit-react";
import { shortTime, dayLabel, formatFileSize } from "./format";
import { Selection } from "./useChatData";
import { MEDIA_TYPES } from "./ConversationFiles";
import { DownloadIcon } from "./Icons";
import { downloadAttachment } from "./download";

type FileHit = { message: CometChat.MediaMessage; where: string; selection: Selection };

/** The "Files" rail tab — every file/image/video/audio attachment shared
 * across all of your conversations, newest first, in one place. */
export default function FilesPanel({
  onClose,
  onOpen,
}: {
  onClose: () => void;
  onOpen: (s: Selection) => void;
}) {
  const [hits, setHits] = useState<FileHit[] | null>(null);
  const [error, setError] = useState(false);
  const meUid = CometChatUIKitLoginListener.getLoggedInUser()?.getUid();

  useEffect(() => {
    let cancelled = false;
    const req = new CometChat.MessagesRequestBuilder()
      .setLimit(40)
      .setCategories([CometChat.CATEGORY_MESSAGE])
      .setTypes(MEDIA_TYPES)
      .build();
    req
      .fetchPrevious()
      .then((list) => {
        if (cancelled) return;
        const out: FileHit[] = ((list as CometChat.BaseMessage[]) || [])
          .filter((m) => MEDIA_TYPES.includes(m.getType()))
          .reverse()
          .map((m) => {
            const message = m as CometChat.MediaMessage;
            let selection: Selection;
            let where: string;
            if (m.getReceiverType() === "group") {
              const receiver = m.getReceiver() as CometChat.Group;
              const name = receiver?.getName?.() || m.getReceiverId();
              selection = { kind: "group", id: m.getReceiverId(), name, subtitle: "Channel" };
              where = `#${name}`;
            } else {
              const sender = m.getSender();
              const other =
                sender?.getUid() === meUid
                  ? { id: m.getReceiverId(), name: (m.getReceiver() as CometChat.User)?.getName?.() || m.getReceiverId() }
                  : { id: sender?.getUid() || "", name: sender?.getName() || "Unknown" };
              selection = { kind: "user", id: other.id, name: other.name, subtitle: "Direct message" };
              where = other.name;
            }
            return { message, where, selection };
          });
        setHits(out);
      })
      .catch((e) => {
        console.warn("files fetch failed", e);
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [meUid]);

  const open = (selection: Selection) => {
    onOpen(selection);
    onClose();
  };

  return (
    <aside className="details-panel later-panel">
      <div className="modal-head">
        <b>Files</b>
        <button className="hbtn" onClick={onClose} aria-label="Close">✕</button>
      </div>

      <div className="modal-section">
        <div className="modal-list">
          {hits === null && !error && <div className="sect-empty">Loading…</div>}
          {error && <div className="sect-empty">Couldn't load files</div>}
          {hits && hits.length === 0 && <div className="sect-empty">No files shared yet</div>}
          {hits?.map((hit) => {
            const attachment = hit.message.getAttachment?.();
            const name = attachment?.getName() || "Attachment";
            const size = formatFileSize(attachment?.getSize());
            const url = attachment?.getUrl();
            return (
              <div className="later-item" key={hit.message.getId()}>
                <button className="later-item__main" onClick={() => open(hit.selection)}>
                  <span className="later-item__text">{name}</span>
                  <span className="later-item__meta">
                    {hit.where}
                    {size ? ` · ${size}` : ""} · {dayLabel(hit.message.getSentAt())} {shortTime(hit.message.getSentAt())}
                  </span>
                </button>
                {url && (
                  <button
                    className="later-item__remove"
                    title={`Download ${name}`}
                    aria-label={`Download ${name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      void downloadAttachment(url, name);
                    }}
                  >
                    <DownloadIcon size={16} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
